import FormWithSections, { FormSection } from "../commons/components/form/FormWithSections";
import FormTitle from "../commons/components/form/FormTitle";
import * as React from "react";
import { useState } from "react";
import Field, { FieldKey } from "../commons/model/form/Field";
import Form from "../commons/components/form/Form";
import sfetch from "../commons/utils/super-fetch";
import AsyncAction from "../AsyncAction";
import { SectionConfiguration } from "../commons/components/form/SectionConfiguration";
import { getPayload } from "../commons/utils/form-utils";
import {
  DentalInfo,
  getSmileSatisfactionI18n,
  SmileSatisfaction,
  smileSatisfactionOrdered,
} from "./DentalInfo";
import BinaryRadioButton from "../commons/components/buttons/BinaryRadioButton";
import RadioInput from "../commons/components/form/input/RadioInput";
import SelectableOption from "../commons/model/SelectableOption";
import { sanitize } from "../commons/utils/sanitize";
import InputState from "../commons/model/form/InputState";

export default function DentalInfoForm({
  dentalInfo,
  fields,
  updateUrl,
  title,
  submitLabel,
  afterSubmit,
}: {
  dentalInfo?: DentalInfo;
  fields: Field[];
  updateUrl: string;
  title?: string;
  submitLabel?: string;
  afterSubmit?: (form: { [key: string]: InputState }) => any;
}) {
  const [hasDentist, setHasDentist] = useState<boolean>(dentalInfo ? dentalInfo.hasDentist : true);
  const [smileSatisfactionState, setSmileSatisfaction] = useState<SmileSatisfaction>(
    dentalInfo ? dentalInfo.smileSatisfaction : null
  );
  const [errorMessage, setErrorMessage] = useState(null);

  const hasSmileSatisfactionField = fields.some(
    (field) => field.key == FieldKey.SMILE_SATISFACTION && field.available
  );
  const smileSatisfactionFieldIsMandatory = fields.some(
    (field) => field.key == FieldKey.SMILE_SATISFACTION && field.mandatory
  );

  const submitDentalInformation = async (form: { [key: string]: InputState }) => {
    let payload = {};
    if (hasDentist) {
      payload = getPayload(
        form,
        fields.map((f) => f.name),
      );
    }
    payload["hasDentist"] = hasDentist;
    if (hasSmileSatisfactionField) {
      payload["smileSatisfaction"] = smileSatisfactionState;
    }

    try {
      await sfetch(updateUrl, {
        method: "PUT",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (afterSubmit) afterSubmit(form);
    } catch (error) {
      setErrorMessage(i18n["Global.TechnicalError"]);
      throw error;
    }
  };

  const sections: SectionConfiguration[] = [
    {
      title: i18n["Form.DentalInformation.Section.DentistInformation"],
      fieldKeys: [FieldKey.PRACTICE_NAME, FieldKey.DENTIST_NAME],
    },
    {
      title: i18n["Form.DentalInformation.Section.Treatment"],
      fieldKeys: [
        FieldKey.LAST_DENTAL_CLEANING,
        FieldKey.PENDING_TREATMENT,
        FieldKey.PENDING_TREATMENT_DESCRIPTION,
      ],
    },
  ];

  const extraProps = {
    practiceName: {
      additionalClass: "medium",
    },
    dentistName: {
      additionalClass: "medium",
    },
    lastDentalCleaning: {
      additionalClass: "one-line",
    },
    pendingTreatment: {
      additionalClass: "one-line",
      options: [
        new SelectableOption(
          "true",
          i18n["Global.Yes"],
          dentalInfo?.hasDentist && dentalInfo?.pendingTreatment === true
        ),
        new SelectableOption(
          "false",
          i18n["Global.No"],
          dentalInfo?.hasDentist && dentalInfo?.pendingTreatment === false
        ),
      ],
    },
    pendingTreatmentDescription: {
      additionalClass: "full-width",
    },
  };

  const initialValueAndExtraProps = fields.reduce((acc, field) => {
    const initialValue = findDentalInformation(field.key, dentalInfo);
    acc[field.name] = {
      initialValue,
      dirty: !!initialValue,
      ...extraProps[field.name],
    };
    return acc;
  }, {});

  return (
    <>
      {title && <FormTitle title={title} />}
      <div className={"react-form"}>
        {errorMessage && (
          <div
            className={"form-general-error message danger" + (!errorMessage ? " none" : "")}
            dangerouslySetInnerHTML={{
              __html: sanitize(errorMessage),
            }}
          />
        )}
        {hasSmileSatisfactionField && (
          <>
            <div className="form-section">
              <div className="section-header">
                <span className="title">
                  {i18n["Form.DentalInformation.Field.SmileSatisfaction.Title"]}
                </span>
              </div>
              <RadioInput
                additionalClass={"big"}
                flexOrientation="horizontal"
                options={smileSatisfactionOrdered.map(
                  (smileSatisfaction) =>
                    new SelectableOption(
                      smileSatisfaction,
                      i18n[getSmileSatisfactionI18n(smileSatisfaction)],
                      smileSatisfaction == smileSatisfactionState
                    )
                )}
                onChange={(inputState) => setSmileSatisfaction(inputState.value)}
                name={"smileSatisfaction"}
              />
            </div>
            <hr className={"hr-secondary"} />
          </>
        )}
        <BinaryRadioButton
          name={"hasDentist"}
          labelTrue={i18n["Form.DentalInformation.Field.HaveDentist"]}
          labelFalse={i18n["Form.DentalInformation.Field.DontHaveDentist"]}
          checked={hasDentist}
          onChange={setHasDentist}
          additionalClass={"full-width"}
        />
        <hr className={"hr-secondary"} />
      </div>
      {hasDentist ? (
        <FormWithSections
          submitLabel={submitLabel ? submitLabel : i18n["Global.Save"]}
          submit={submitDentalInformation}
          submitDisabled={() => smileSatisfactionFieldIsMandatory && !smileSatisfactionState}
        >
          {sections.map((section) => (
            <FormSection title={section.title} key={section.title}>
              {Form.getFieldComponents(
                fields.filter((field) => section.fieldKeys.includes(field.key)),
                initialValueAndExtraProps,
                FieldKey.getTitle
              )}
            </FormSection>
          ))}
        </FormWithSections>
      ) : (
        <div className={"h-flex centered action-container"}>
          <AsyncAction
            buttonLabel={submitLabel ? submitLabel : i18n["Global.Save"]}
            promise={() => submitDentalInformation(null)}
            buttonType={"button-primary"}
            disabled={smileSatisfactionFieldIsMandatory && !smileSatisfactionState}
          />
        </div>
      )}
    </>
  );

  function findDentalInformation(key: FieldKey, dentalInformation: DentalInfo) {
    switch (key) {
      case FieldKey.PRACTICE_NAME:
        return dentalInformation?.practiceName;
      case FieldKey.DENTIST_NAME:
        return dentalInformation?.dentistName;
      case FieldKey.LAST_DENTAL_CLEANING:
        return dentalInformation?.lastDentalCleaning
          ? new Date(dentalInformation?.lastDentalCleaning)
          : null;
      case FieldKey.PENDING_TREATMENT:
        return dentalInformation?.pendingTreatment;
      case FieldKey.PENDING_TREATMENT_DESCRIPTION:
        return dentalInformation?.pendingTreatmentDescription;
      case FieldKey.SMILE_SATISFACTION:
        return dentalInformation?.smileSatisfaction;
      default:
        return null;
    }
  }
}
